import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ChevronRight, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { useLocalize } from '@/hooks/useLandingContent';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Markdown } from '@/components/forum/Markdown';
import { ReadingProgress } from '@/components/blog/ReadingProgress';
import { TableOfContents } from '@/components/blog/TableOfContents';
import { NewsShare } from '@/components/news/NewsShare';
import { NewsBookmarkButton } from '@/components/news/NewsBookmarkButton';
import { estimateReadingTime } from '@/lib/readingTime';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PUBLIC_BASE = 'https://www.ethiorealestates.com';

export default function NewsDetail() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const localize = useLocalize();
  const articleRef = useRef<HTMLElement>(null);

  const { data: article, isLoading } = useQuery({
    queryKey: ['news-detail', slug],
    queryFn: async () => {
      const isUuid = UUID_RE.test(slug);
      const col = isUuid ? 'id' : 'slug';
      const { data } = await supabase
        .from('news_articles')
        .select('*')
        .eq(col, slug)
        .eq('is_published', true)
        .maybeSingle();
      return data;
    },
  });

  const { data: related } = useQuery({
    queryKey: ['news-related', article?.id, (article as any)?.category, ((article as any)?.tags || []).join(',')],
    enabled: !!article,
    queryFn: async () => {
      const limit = 4;
      const exclude = article!.id;
      let pool: any[] = [];
      const cat = (article as any).category as string | null;
      const tags = ((article as any).tags || []) as string[];

      if (cat) {
        const { data } = await supabase
          .from('news_articles')
          .select('id, slug, title, cover_url, summary, published_at, category, tags')
          .eq('is_published', true).eq('category', cat).neq('id', exclude)
          .order('is_pinned', { ascending: false }).order('is_featured', { ascending: false }).order('published_at', { ascending: false }).limit(limit);
        pool = data || [];
      }
      if (pool.length < limit && tags.length) {
        const { data } = await supabase
          .from('news_articles')
          .select('id, slug, title, cover_url, summary, published_at, category, tags')
          .eq('is_published', true).neq('id', exclude)
          .overlaps('tags', tags).limit(limit);
        const seen = new Set(pool.map((p) => p.id));
        for (const r of data || []) if (!seen.has(r.id) && pool.length < limit) pool.push(r);
      }
      if (pool.length < limit) {
        const { data } = await supabase
          .from('news_articles')
          .select('id, slug, title, cover_url, summary, published_at, category, tags')
          .eq('is_published', true).neq('id', exclude)
          .order('is_pinned', { ascending: false }).order('is_featured', { ascending: false }).order('published_at', { ascending: false }).limit(limit);
        const seen = new Set(pool.map((p) => p.id));
        for (const r of data || []) if (!seen.has(r.id) && pool.length < limit) pool.push(r);
      }
      return pool;
    },
  });

  // View tracking — once per session per article
  useEffect(() => {
    if (!article?.id) return;
    const key = `news_view_${article.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    supabase.rpc('record_news_view', {
      _article_id: article.id,
      _referrer: document.referrer || null,
      _owner_token: null,
    }).then(() => {});
  }, [article?.id]);

  const fmtDate = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const bodyText = useMemo(() => {
    if (!article) return '';
    return localize((article as any).body) || '';
  }, [article, localize]);
  const readMinutes = useMemo(() => estimateReadingTime(bodyText), [bodyText]);

  if (isLoading) {
    return <Layout><div className="container py-20 text-center text-muted-foreground">Loading…</div></Layout>;
  }
  if (!article) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <h1 className="font-display text-2xl font-bold mb-2">Article not found</h1>
          <Button variant="outline" onClick={() => navigate('/news')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to news
          </Button>
        </div>
      </Layout>
    );
  }

  // Canonical redirect from /news/{uuid} → /news/{slug}
  const canonicalSlug = (article as any).slug as string | undefined;
  if (UUID_RE.test(slug) && canonicalSlug && canonicalSlug !== slug) {
    return <Navigate to={`/news/${canonicalSlug}`} replace />;
  }

  const title = localize(article.title);
  const summary = localize((article as any).summary);
  const category = (article as any).category as string | null;
  const tags = ((article as any).tags || []) as string[];
  const authorName = (article as any).author_name || 'Editorial Team';
  const viewCount = (article as any).view_count as number | undefined;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    description: summary || title,
    datePublished: article.published_at,
    dateModified: (article as any).updated_at || article.published_at,
    image: (article as any).cover_url ? [(article as any).cover_url] : undefined,
    author: { '@type': 'Person', name: authorName },
    publisher: {
      '@type': 'Organization',
      name: 'Ethio Real Estates',
      logo: { '@type': 'ImageObject', url: `${PUBLIC_BASE}/favicon.ico` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${PUBLIC_BASE}/news/${canonicalSlug || article.id}` },
    articleSection: category || undefined,
    keywords: tags.length ? tags.join(', ') : undefined,
  };

  return (
    <Layout>
      <SEOHead
        title={`${title} — Ethio Real Estates News`}
        description={summary || title}
        canonical={`/news/${canonicalSlug || article.id}`}
        ogImage={(article as any).og_image_url || (article as any).cover_url || undefined}
        ogType="article"
        jsonLd={jsonLd}
      />
      <ReadingProgress />

      <div className="container max-w-7xl py-8 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-10">
        <aside>
          <TableOfContents markdown={bodyText} containerRef={articleRef} />
        </aside>

        <article ref={articleRef} className="max-w-3xl w-full mx-auto">
          <Link to="/news" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="h-4 w-4" /> Back to all news
          </Link>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {category && (
              <Link to={`/news?category=${encodeURIComponent(category)}`}>
                <Badge variant="secondary" className="uppercase tracking-wide">{category}</Badge>
              </Link>
            )}
            {tags.map((t) => (
              <Link key={t} to={`/news?tag=${encodeURIComponent(t)}`}>
                <Badge variant="outline" className="font-normal">#{t}</Badge>
              </Link>
            ))}
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4">{title}</h1>

          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-5 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold inline-flex items-center justify-center">
                {authorName.charAt(0).toUpperCase()}
              </span>
              By {authorName}
            </span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(article.published_at)}</span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {readMinutes} min read</span>
            {typeof viewCount === 'number' && (
              <>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {viewCount}</span>
              </>
            )}
          </div>

          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <NewsShare title={title} />
            <NewsBookmarkButton articleId={article.id} />
          </div>

          {(article as any).cover_url && (
            <img
              src={optimizedImageUrl((article as any).cover_url, { width: 1200, height: 700, quality: 80 })}
              alt={title}
              className="w-full aspect-[16/9] object-cover rounded-xl mb-8"
            />
          )}

          {summary && <p className="text-lg text-muted-foreground leading-relaxed mb-6">{summary}</p>}

          {bodyText ? (
            <Markdown className="prose-base">{bodyText}</Markdown>
          ) : (
            <p className="text-muted-foreground italic">Full article coming soon.</p>
          )}

          {tags.length > 0 && (
            <div className="mt-10 pt-6 border-t flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-muted-foreground mr-1">Tagged:</span>
              {tags.map((t) => (
                <Link key={t} to={`/news?tag=${encodeURIComponent(t)}`}>
                  <Badge variant="outline" className="font-normal hover:border-primary">#{t}</Badge>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-8 rounded-xl border bg-card p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 text-primary font-bold inline-flex items-center justify-center">
              {authorName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">Reported by</div>
              <div className="font-semibold">{authorName}</div>
            </div>
          </div>
        </article>
      </div>

      {related && related.length > 0 && (
        <section className="border-t mt-12 py-10 bg-muted/30">
          <div className="container max-w-5xl">
            <h2 className="font-display text-xl font-bold mb-5">Related stories</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((r: any) => (
                <Link
                  key={r.id}
                  to={`/news/${r.slug || r.id}`}
                  className="block rounded-xl bg-card border overflow-hidden hover:shadow-md transition group"
                >
                  {r.cover_url && (
                    <img
                      src={optimizedImageUrl(r.cover_url, { width: 400, height: 260, quality: 75 })}
                      alt=""
                      className="w-full aspect-[16/11] object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="p-3">
                    <div className="text-[11px] text-muted-foreground mb-1 inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {fmtDate(r.published_at)}
                    </div>
                    <div className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary">
                      {localize(r.title)}
                    </div>
                    <span className="text-xs text-primary font-semibold mt-2 inline-flex items-center gap-1">
                      Read <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
